import { useState } from 'react';

/**
 * Dev-only crib sheet for the seeded sign-ins, rendered below the Login card.
 *
 * The list mirrors `infra/keycloak/mvcp-realm.json` (and `uat/support/roles.ts`,
 * which cannot be imported here — it is Node-only); `DevCredentials.test.tsx`
 * reads that realm file and fails if the two drift. Kept in a module of its own
 * so `import.meta.env.DEV` gates the whole thing: Vite replaces that with the
 * literal `false` in `vite build`, so the branch — and every credential string
 * with it — is dropped from production bundles. A runtime `VITE_*` flag would
 * ship the strings.
 *
 * That elimination is asserted against a real build artifact by
 * `scripts/check-web-bundle-clean.sh` (a CI step), not just by the unit test —
 * the gate is a compile-time constant, so only the emitted bundle can prove it.
 */

interface DevAccount {
  email: string;
  password: string;
  role: string;
  /** Why a tester would reach for this principal. */
  goodFor: string;
}

/**
 * The seeded principals. Dev-only realm defaults — no real secrets here.
 * Deliberately NOT exported: a module-level export that prod code never uses is
 * one refactor away from keeping these strings alive past the gate.
 */
const DEV_ACCOUNTS: readonly DevAccount[] = [
  {
    email: 'admin@mvcp.test',
    password: 'Passw0rd!',
    role: 'System Administrator',
    goodFor: 'All companies, cross-tenant — Admin Console, curation, verification',
  },
  {
    email: 'manager-a@demo.test',
    password: 'Passw0rd!',
    role: 'Compliance Manager',
    goodFor: 'Maritime Demo Co. A — cockpit, vessels, cross-company propagation',
  },
  {
    email: 'manager-c@demo.test',
    password: 'Passw0rd!',
    role: 'Compliance Manager',
    goodFor: 'Non-maritime Demo Co. C — maritime frameworks hidden',
  },
  {
    email: 'vendor-shared@demo.test',
    password: 'Passw0rd!',
    role: 'Vendor',
    goodFor: 'Shared demo vendor — portal profile, alerts, declare/upload',
  },
];

/**
 * The shared password, or `null` when they ever diverge — in which case each row
 * shows its own rather than the header claiming a single one for everybody.
 *
 * A FUNCTION, not a module-level const: `new Set(DEV_ACCOUNTS.map(…))` evaluated at
 * module scope is not provably side-effect-free, so Rollup has to keep it — and with
 * it `DEV_ACCOUNTS` — alive in production even though the gate makes every reader
 * unreachable. That regression shipped the credentials into `dist/assets/*.js` and
 * was caught by `scripts/check-web-bundle-clean.sh`. Called only past the gate, so
 * the whole thing stays inside the dead branch.
 */
function sharedPassword(): string | null {
  return new Set(DEV_ACCOUNTS.map((a) => a.password)).size === 1
    ? (DEV_ACCOUNTS[0]?.password ?? null)
    : null;
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  width: '100%',
  padding: '7px 9px',
  textAlign: 'left',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  font: 'inherit',
  cursor: 'pointer',
};

/**
 * Renders nothing outside dev. `onPick` fills the Login form with an account's
 * credentials — it deliberately does NOT submit, so the tester still sees which
 * principal they are about to become.
 */
export function DevCredentials({
  onPick,
}: {
  onPick: (account: { email: string; password: string }) => void;
}): React.JSX.Element | null {
  // Filling a masked password field is invisible, and the pick clears Login's
  // error region — so without this there is no feedback that the click landed.
  const [picked, setPicked] = useState<string | null>(null);

  if (!import.meta.env.DEV) return null;

  const shared = sharedPassword();

  return (
    <aside
      className="card stack"
      aria-labelledby="dev-credentials-heading"
      style={{ width: 'min(400px, 100%)', gap: 10 }}
    >
      <div className="stack" style={{ gap: 4 }}>
        <h2
          id="dev-credentials-heading"
          style={{ margin: 0, fontSize: 13, fontWeight: 800 }}
        >
          Seeded logins (dev builds only)
        </h2>
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          {shared === null ? (
            <>Passwords are per-account below — click one to fill the form.</>
          ) : (
            <>
              Password for all {DEV_ACCOUNTS.length}: <code>{shared}</code> — click
              one to fill the form.
            </>
          )}
        </p>
      </div>

      <div className="stack" style={{ gap: 6 }}>
        {DEV_ACCOUNTS.map((account, index) => {
          // Name the row from the email alone and demote the blurb to a
          // description: the default accessible name would be the whole
          // concatenated row, which is unusable as a locator and as speech.
          // Index, not a slugged email — slugging is not injective, so
          // `manager-a@` and `manager.a@` would collide into duplicate ids.
          const id = `dev-account-${index}`;
          return (
            <button
              key={account.email}
              type="button"
              aria-labelledby={id}
              aria-describedby={`${id}-desc`}
              onClick={() => {
                onPick({ email: account.email, password: account.password });
                setPicked(account.email);
              }}
              style={rowStyle}
            >
              <code id={id} style={{ fontSize: 12.5, fontWeight: 700 }}>
                Use {account.email}
              </code>
              <span id={`${id}-desc`} className="muted" style={{ fontSize: 11.5 }}>
                {account.role} — {account.goodFor}
                {shared === null ? ` — ${account.password}` : ''}
              </span>
            </button>
          );
        })}
      </div>

      <p role="status" aria-live="polite" className="muted" style={{ margin: 0, fontSize: 11.5 }}>
        {picked === null ? '' : `Form filled with ${picked}.`}
      </p>

      <p className="muted" style={{ margin: 0, fontSize: 11.5 }}>
        Demo tenants need <code>pnpm --filter @mvcp/api build && pnpm --filter @mvcp/api seed:demo</code>{' '}
        first — the seed runs from <code>dist/</code>. Demo Maritime Co. B has no login by
        design — read it as admin.
      </p>
    </aside>
  );
}
